// Lib
import React, { ReactElement, RefObject, useEffect, useRef, useState, WheelEventHandler } from 'react';
import classNames from 'classnames';
import { debounce } from 'lodash';

// Components
import Icon from '../icons/Icon';

// Hooks
import useSliderHandleEventHandlers from './hooks/useSliderHandleEventHandlers';
import useSliderRailEventHandlers from './hooks/useSliderRailEventHandlers';
import useSliderButtonsEventHandlers from './hooks/useSliderButtonsEventHandlers';

// Utils
import { getSliderRailPositionFromValue } from './sliderUtils';

// Styles
import './Slider.scss';

export interface SliderProps {
    className: string;

    min: number;
    max: number;
    value: number;
    unit?: string;

    // Value to reset to when user double clicks on slider handle
    resetValue?: number;

    // Checkpoint is a value on the slider that is the midpoint of the slider. If the user clicks a point on the
    // slider rail that is over the checkpoint, it will first stop at the checkpoint.
    checkpoint?: number;

    incrementValue?: (currentValue: number) => number;
    decrementValue?: (currentValue: number) => number;

    // Set manual to true if you don't want the slider to set the displayed slider value (slider handle value and
    // position), and want to set it manually from the parent
    manual?: boolean;

    onChange: (currentValue: number) => void;
    onWheel?: WheelEventHandler<HTMLDivElement>;

    handleRef: RefObject<HTMLButtonElement>;
}

const Slider = (props: SliderProps): ReactElement => {
    const { value, unit = '', checkpoint, className, onChange, manual = false, handleRef, onWheel } = props;

    const sliderHandleRef = handleRef || useRef<HTMLButtonElement | null>(null);
    const sliderHandleContainerRef = useRef<HTMLDivElement | null>(null);
    const sliderHandleContainerRectRef = useRef<DOMRect | null>(null);

    const [currentValue, setCurrentValue] = useState(value);

    useEffect(() => {
        // Get the bounding client rect of the slider handle container
        if (!sliderHandleContainerRef.current) return;

        sliderHandleContainerRectRef.current = sliderHandleContainerRef.current.getBoundingClientRect();

        const debouncedOnResize = debounce(() => {
            if (!sliderHandleContainerRef.current) return;

            sliderHandleContainerRectRef.current = sliderHandleContainerRef.current.getBoundingClientRect();
        }, 200);

        window.addEventListener('resize', debouncedOnResize);

        return () => {
            window.removeEventListener('resize', debouncedOnResize);
        };
    }, []);

    // On an external value change, update the Slider's current value to match
    useEffect(() => {
        setCurrentValue(value);
    }, [value]);

    // On a Slider current value change, call onChange to notify parent components
    useEffect(() => {
        onChange(currentValue);
    }, [currentValue]);

    const sliderHandleEventHandlers = useSliderHandleEventHandlers({
        ...props,
        currentValue,
        setCurrentValue,
        sliderHandleContainerRectRef,
    });

    const sliderRailEventHandlers = useSliderRailEventHandlers({
        ...props,
        currentValue,
        setCurrentValue,
        sliderHandleContainerRectRef,
    });

    const { decrementButtonClick, incrementButtonClick } = useSliderButtonsEventHandlers({
        ...props,
        currentValue,
        setCurrentValue,
    });

    // If on manual mode, only display value that is passed in from parent, otherwise, show value that is calculated
    // by this component
    const displayValue = manual ? value : currentValue;

    return (
        <div className={classNames('Slider', className)} onWheel={onWheel}>
            <button onClick={decrementButtonClick}>
                <Icon name="minus" />
            </button>
            <div className="slider-container">
                <div className="slider-rail-container" {...sliderRailEventHandlers}>
                    <span className="slider-rail"></span>
                    {checkpoint && <span className="slider-rail-checkpoint"></span>}
                </div>
                <div className="slider-handle-container" ref={sliderHandleContainerRef}>
                    <button
                        className="slider-handle"
                        ref={sliderHandleRef}
                        style={{
                            left: `${getSliderRailPositionFromValue(displayValue, props) * 100}%`,
                        }}
                        {...sliderHandleEventHandlers}
                    >
                        {Math.round(displayValue)}
                        {unit}
                    </button>
                </div>
            </div>
            <button onClick={incrementButtonClick}>
                <Icon name="plus" />
            </button>
        </div>
    );
};

export default Slider;
